import clsx from 'clsx';
import React, { ReactNode, useRef, useState } from 'react';
import ChevronDown from '../../assets/icons/select-chevron-down.svg?react';
import { useClickOutside } from '../../utils/clickOutSide';
import WarningIcon from './../../assets/icons/warning-icon.svg?react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import { useLocalStorage } from '@uidotdev/usehooks';
import { LoginResponseData } from '@EHSNavigator/backend';

interface MultiAssigneeDropDownProps {
  otherClasses?: {
    container?: string;
    label?: string;
    button?: string;
    listContainer?: string;
    list?: string;
    tagClasses?: string;
  };
  label?: string | ReactNode;
  disabled?: boolean;
  errorMessage?: string;
  value?: string[];
  list?: {
    label: string;
    value: string;
  }[];
  placeholder?: string;
  onSelect: (e: string) => void;
  handleRemove: (e: string) => void;
  singleSelect?: boolean;
  withSearch?: boolean;
  fullLabel?: boolean;
}

const MultiAssigneeDropDown: React.FC<MultiAssigneeDropDownProps> = ({
  otherClasses,
  label,
  disabled = false,
  errorMessage = '',
  withSearch,
  value = [],
  list = [],
  placeholder = '',
  onSelect,
  handleRemove,
  singleSelect = false,
  fullLabel = false,
}) => {
  const multiAssigneeDropDownClasses = clsx(
    otherClasses?.container,
    'flex flex-col gap-2.5 items-start w-fit relative',
  );
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );

  const [toggle, setToggle] = useState<boolean>(false);
  const ref = useRef(null);
  const [query, setQuery] = useState('');
  useClickOutside({ ref, setToggle });
  const labelClasses = clsx(
    otherClasses?.label,
    'text-form-label -tracking-form-label font-inter-variable font-bold',
    disabled ? `text-neutral-tones-text/35` : `text-neutral-tones-text `,
  );

  const buttonClasses = clsx(
    otherClasses?.button,
    'w-full text-left h-60 global-transition ring border-transparent flex items-center gap-2 justify-between font-normal font-inter-variable  disabled:ring-neutral-tones-text/35 disabled:text-neutral-tones-text/35 disabled:cursor-not-allowed cursor-pointer',
    errorMessage !== ''
      ? `ring-brand-posh-pink ring-2`
      : toggle
        ? `ring-brand-byzantine ring-2`
        : `${value?.length === 0 ? `ring-neutral-tones-text/50 ` : `ring-neutral-tones-text`}`,
    value?.length === 0
      ? 'text-neutral-tones-text/35'
      : 'text-neutral-tones-text',
    'px-5 py-15 bg-transparent text-base -tracking-form-input  rounded-10 ',
  );
  const listContainerClasses = clsx(
    otherClasses?.listContainer,
    'bg-light-translucent-background dark:bg-dark-translucent-background rounded-10 ring-neutral-tones-text/35 ring text-left absolute z-10 top-24 w-full max-h-64 overflow-auto global-scrollbar-style',
    withSearch ? 'pb-15  px-5' : 'py-15 px-5 ',
  );
  const listClasses = clsx(
    otherClasses?.list,
    'border-b border-b-neutral-tones-text/10 first:!pt-0 py-2.5 last:pb-0 last:border-0 last:pt-2.5 cursor-pointer',
  );

  const filteredPeople =
    query === ''
      ? list
      : list.filter((person) => {
          return person.label.toLowerCase().includes(query.toLowerCase());
        });

  return (
    <div
      ref={ref}
      className={multiAssigneeDropDownClasses}
      data-testid="multi-assignee-drop-down"
    >
      {label && (
        <label
          onClick={() => !disabled && setToggle(!toggle)}
          className={labelClasses}
        >
          {label}
        </label>
      )}
      <button
        disabled={disabled}
        className={buttonClasses}
        onClick={(e) => {
          e.preventDefault();
          setToggle(!toggle);
          setQuery('');
        }}
      >
        <div className="flex flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex flex-1 items-center gap-2">
            {Array.isArray(value) ? (
              value?.length > 0 ? (
                value?.map((item) => (
                  <div
                    key={item}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className={clsx(
                      'font-inter-variable bg-brand-byzantine relative flex h-10 min-h-10 w-10 min-w-10 cursor-auto items-center justify-center rounded-full text-base font-bold text-white uppercase',
                      otherClasses?.tagClasses,
                    )}
                  >
                    {fullLabel
                      ? list?.find((node) => node?.value === item)?.label
                      : list
                          ?.find((node) => node?.value === item)
                          ?.label.charAt(0)}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove(item);
                      }}
                      className="bg-brand-posh-pink ring-brand-posh-pink/50 absolute top-1 right-0 flex h-3 w-3 cursor-pointer items-center justify-center rounded-full ring-2"
                    >
                      <SidebarCrossIcon className="h-1.5 w-1.5 [&>g>path]:fill-white" />
                    </button>
                  </div>
                ))
              ) : (
                placeholder
              )
            ) : value ? (
              <>
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className={clsx(
                    'font-inter-variable bg-brand-byzantine relative flex h-10 min-h-10 w-10 min-w-10 cursor-auto items-center justify-center rounded-full text-base font-bold text-white uppercase',
                  )}
                >
                  {list?.find((node) => node?.value === value)?.label.charAt(0)}{' '}
                  {singleSelect && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemove?.(value);
                      }}
                      className="bg-brand-posh-pink ring-brand-posh-pink/50 absolute top-1 right-0 flex h-3 w-3 cursor-pointer items-center justify-center rounded-full ring-2"
                    >
                      <SidebarCrossIcon className="h-1.5 w-1.5 [&>g>path]:fill-white" />
                    </button>
                  )}
                </div>
                {list?.find((node) => node?.value === value)?.label}
              </>
            ) : (
              placeholder
            )}
          </div>
        </div>
        <ChevronDown
          className={clsx(
            'global-transition',
            errorMessage !== ''
              ? `[&>path]:fill-brand-posh-pink`
              : disabled
                ? '[&>path]:fill-neutral-tones-text/35'
                : '[&>path]:fill-neutral-tones-text',
            toggle ? 'rotate-180' : 'rotate-0',
          )}
        />
      </button>
      {toggle && (
        <>
          {filteredPeople?.length === 0 ? (
            <ul className={listContainerClasses}>
              {withSearch && (
                <li className="bg-light-translucent-background dark:bg-dark-translucent-background sticky top-0 z-10 pt-15 pb-2">
                  <input
                    type="text"
                    className="bg-card-backgrounds/5 text-neutral-tones-text focus:border-brand-byzantine focus:ring-brand-byzantine/50 w-full rounded border border-transparent px-3 py-1 text-sm font-normal ring-2 ring-transparent outline-0 transition-all duration-300"
                    value={query}
                    placeholder="Search assignee  "
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </li>
              )}
              <li
                className={
                  'border-b-neutral-tones-text/10 cursor-pointer border-b py-2.5 first:pt-0 last:border-0 last:pb-0'
                }
              >
                <button
                  type="button"
                  className={clsx(
                    'font-inter-variable -tracking-form-input text-neutral-tones-text flex w-full cursor-pointer items-center gap-2 text-left text-base font-normal capitalize',
                  )}
                >
                  <WarningIcon className="[&>g>path]:fill-brand-byzantine h-6 w-6 lg:h-6 lg:w-6" />
                  No Users Found
                </button>
              </li>
            </ul>
          ) : (
            <ul className={listContainerClasses}>
              {withSearch && (
                <li className="bg-light-translucent-background dark:bg-dark-translucent-background sticky top-0 z-10 pt-15 pb-2">
                  <input
                    type="text"
                    className="bg-card-backgrounds/5 text-neutral-tones-text focus:border-brand-byzantine focus:ring-brand-byzantine/50 w-full rounded border border-transparent px-3 py-1 text-sm font-normal ring-2 ring-transparent outline-0 transition-all duration-300"
                    value={query}
                    placeholder="Search assignee  "
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </li>
              )}
              {filteredPeople?.map((node, index) => (
                <li key={index} className={listClasses}>
                  <button
                    className={clsx(
                      'font-inter-variable flex w-full cursor-pointer items-center gap-2 text-left',
                      Array.isArray(value)
                        ? value?.some((item) => item === node?.value)
                          ? 'text-brand-byzantine font-bold'
                          : 'hover:text-neutral-tones-text text-neutral-tones-text/80 font-normal'
                        : value === node?.value
                          ? 'text-brand-byzantine font-bold'
                          : 'hover:text-neutral-tones-text text-neutral-tones-text/80 font-normal',
                      '-tracking-form-input text-base',
                    )}
                    onClick={(e) => {
                      e.preventDefault();
                      onSelect(node?.value);
                      setQuery('');
                      // setToggle(false);
                    }}
                  >
                    <div
                      className={clsx(
                        'font-inter-variable bg-brand-byzantine relative flex h-9 min-h-9 w-9 min-w-9 cursor-auto items-center justify-center rounded-full text-base font-bold text-white uppercase',
                      )}
                    >
                      {node?.label.charAt(0)}
                    </div>
                    <span className="line-clamp-1">{node?.label}</span>{' '}
                    {node?.value === userInfo?.user?.id && (
                      <span className="bg-brand-byzantine/30 text-brand-byzantine font-inter-variable rounded-full px-2 py-0.5 text-xs font-bold">
                        You
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {errorMessage !== '' && (
        <span className="font-inter-variable text-brand-posh-pink absolute -bottom-5 left-0 text-xs font-normal">
          {errorMessage}
        </span>
      )}
    </div>
  );
};

export default MultiAssigneeDropDown;
